import { SizedWord } from './wordCloudSizing'
import { Quat, quatRotateVector } from './wordCloudQuaternion'

export interface SphereWord extends SizedWord {
  x: number
  y: number
  z: number
}

export interface ProjectedSphereWord extends SphereWord {
  screenX: number
  screenY: number
  opacity: number
  depthScale: number
  visible: boolean
}

const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5))

/** Evenly distribute words on a unit sphere using a Fibonacci lattice. */
export function placeWordsOnSphere(words: SizedWord[]): SphereWord[] {
  const count = words.length
  if (!count) {
    return []
  }

  return words.map((word, index) => {
    const y = count === 1 ? 0 : 1 - (index / (count - 1)) * 2
    const radiusAtY = Math.sqrt(Math.max(0, 1 - y * y))
    const theta = GOLDEN_ANGLE * index

    return {
      ...word,
      x: Math.cos(theta) * radiusAtY,
      y,
      z: Math.sin(theta) * radiusAtY,
    }
  })
}

export function projectSphereWords(
  words: SphereWord[],
  width: number,
  height: number,
  rotation: Quat,
): ProjectedSphereWord[] {
  if (!words.length || width <= 0 || height <= 0) {
    return []
  }

  const centerX = width / 2
  const centerY = height / 2
  const radius = Math.min(width, height) * 0.38

  return words
    .map((word) => {
      const rotated = quatRotateVector(rotation, { x: word.x, y: word.y, z: word.z })
      const depth = (rotated.z + 1) / 2

      return {
        ...word,
        screenX: centerX + rotated.x * radius,
        screenY: centerY + rotated.y * radius,
        opacity: 0.08 + depth * 0.92,
        depthScale: 0.35 + depth * 0.65,
        visible: true,
        z: rotated.z,
      }
    })
    .sort((a, b) => a.z - b.z)
}
